import { useEffect, useState } from "react";
import { Instagram, Youtube, Facebook, Film, ExternalLink } from "lucide-react";
import PageHero from "../components/PageHero";
import { fetchReels } from "../lib/api";
import { SCHOOL } from "../lib/site";

function getEmbedUrl(reel) {
  const url = reel.url || "";
  // Instagram
  let m = url.match(/instagram\.com\/(?:reel|p|tv)\/([\w-]+)/);
  if (m) return `https://www.instagram.com/p/${m[1]}/embed/captioned/`;
  // YouTube Shorts or regular
  m = url.match(/youtube\.com\/shorts\/([\w-]+)/) || url.match(/youtube\.com\/watch\?v=([\w-]+)/) || url.match(/youtu\.be\/([\w-]+)/);
  if (m) return `https://www.youtube.com/embed/${m[1]}`;
  // Facebook video / reel
  m = url.match(/facebook\.com\/(?:reel|watch|.*\/videos)\/([\w]+)/);
  if (m) return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(url)}&show_text=false`;
  return null;
}

function getPlatformIcon(platform) {
  if (platform === "instagram") return Instagram;
  if (platform === "youtube") return Youtube;
  if (platform === "facebook") return Facebook;
  return Film;
}

export default function Reels() {
  const [reels, setReels] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchReels()
      .then((r) => {
        setReels(r);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  return (
    <div data-testid="page-reels">
      <PageHero
        eyebrow="Video · Social"
        title="Reels"
        italic="dai nostri social."
        subtitle="Esibizioni, momenti di scuola, dietro le quinte. I video brevi che pubblichiamo su Instagram, Facebook e YouTube — tutti in un unico spazio."
      />

      <section className="py-20 px-6 md:px-12 lg:px-24 max-w-[1400px] mx-auto">
        {/* Social header */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-[#A3133A]/15 border border-[#A3133A]/15 mb-16">
          <a
            href={SCHOOL.social.instagram}
            target="_blank"
            rel="noreferrer"
            data-testid="reels-social-instagram"
            className="bg-[#FBFBF9] p-7 flex items-center gap-4 hover:bg-[#A3133A]/5 transition group"
          >
            <Instagram size={28} className="text-[#A3133A]" />
            <div className="flex-1">
              <div className="text-[10px] tracking-eyebrow text-[#A3133A]">Instagram</div>
              <div className="font-serif-display text-lg text-[#1C1C1C]">@tms_amiciperlamusicaaps</div>
            </div>
            <ExternalLink size={14} className="text-[#5C5C5C] group-hover:text-[#A3133A]" />
          </a>
          <a
            href={SCHOOL.social.facebook}
            target="_blank"
            rel="noreferrer"
            data-testid="reels-social-facebook"
            className="bg-[#FBFBF9] p-7 flex items-center gap-4 hover:bg-[#A3133A]/5 transition group"
          >
            <Facebook size={28} className="text-[#A3133A]" />
            <div className="flex-1">
              <div className="text-[10px] tracking-eyebrow text-[#A3133A]">Facebook</div>
              <div className="font-serif-display text-lg text-[#1C1C1C]">Amici per la Musica APS</div>
            </div>
            <ExternalLink size={14} className="text-[#5C5C5C] group-hover:text-[#A3133A]" />
          </a>
          <a
            href={SCHOOL.social.youtube}
            target="_blank"
            rel="noreferrer"
            data-testid="reels-social-youtube"
            className="bg-[#FBFBF9] p-7 flex items-center gap-4 hover:bg-[#A3133A]/5 transition group"
          >
            <Youtube size={28} className="text-[#A3133A]" />
            <div className="flex-1">
              <div className="text-[10px] tracking-eyebrow text-[#A3133A]">YouTube</div>
              <div className="font-serif-display text-lg text-[#1C1C1C]">@amiciperlamusicaaps</div>
            </div>
            <ExternalLink size={14} className="text-[#5C5C5C] group-hover:text-[#A3133A]" />
          </a>
        </div>

        {/* Reels grid */}
        {!loaded ? (
          <div className="text-center py-20 text-[#5C5C5C]">Caricamento...</div>
        ) : reels.length === 0 ? (
          <div className="bg-[#FBFBF9] border border-[#A3133A]/15 p-12 md:p-20 text-center" data-testid="reels-empty">
            <Film size={48} className="text-[#A3133A] mx-auto mb-6 opacity-60" />
            <h3 className="font-serif-display text-3xl text-[#1C1C1C] mb-4">Stiamo per pubblicare i primi reel.</h3>
            <p className="text-[#5C5C5C] max-w-2xl mx-auto leading-relaxed mb-8">
              Nel frattempo, seguici sui nostri canali social per vedere in tempo reale esibizioni,
              momenti di scuola e contenuti speciali dei nostri allievi e docenti.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <a
                href={SCHOOL.social.instagram}
                target="_blank"
                rel="noreferrer"
                className="bg-[#A3133A] text-white px-6 py-3 text-[12px] tracking-eyebrow rounded-sm inline-flex items-center gap-2 hover:bg-[#8B0F2F]"
              >
                <Instagram size={14} /> Seguici su Instagram
              </a>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {reels.map((reel) => {
              const embed = getEmbedUrl(reel);
              const Icon = getPlatformIcon(reel.platform);
              return (
                <article key={reel.id} data-testid={`reel-${reel.id}`} className="bg-[#FBFBF9] border border-[#A3133A]/15 flex flex-col">
                  {embed ? (
                    <div className="aspect-[9/16] overflow-hidden bg-black relative">
                      <iframe
                        src={embed}
                        title={reel.titolo || "Reel"}
                        className="w-full h-full"
                        allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        loading="lazy"
                        frameBorder="0"
                        scrolling="no"
                        data-testid={`reel-embed-${reel.id}`}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[9/16] bg-[#A3133A]/5 flex items-center justify-center">
                      <a href={reel.url} target="_blank" rel="noreferrer" className="text-[#A3133A] text-sm link-underline">
                        Apri il video
                      </a>
                    </div>
                  )}
                  <div className="p-5 flex-1 flex flex-col">
                    <div className="flex items-center gap-2 text-[10px] tracking-eyebrow text-[#A3133A] mb-2">
                      <Icon size={12} />
                      <span>{(reel.platform || "social").toUpperCase()}</span>
                    </div>
                    {reel.titolo && <h3 className="font-serif-display text-xl text-[#1C1C1C] mb-2 leading-tight">{reel.titolo}</h3>}
                    {reel.didascalia && <p className="text-sm text-[#5C5C5C] leading-relaxed">{reel.didascalia}</p>}
                    <a
                      href={reel.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`reel-open-${reel.id}`}
                      className="mt-4 text-[12px] tracking-eyebrow text-[#A3133A] inline-flex items-center gap-2 link-underline self-start"
                    >
                      Apri sul social <ExternalLink size={12} />
                    </a>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
